import React, { useRef, useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import '../styles/MapView.css';
import { useTheme } from '@mui/material/styles';
import { icons, highlightedIcons } from "./icons";
import { Paper, Typography, Box } from '@mui/material';
import API_BASE_URL from '../config';

// --- Legend Component ---
const MapLegend = () => {
  const theme = useTheme();
  const legendColor = '#d73027'; // Red color used for critical assets

  return (
    <Paper
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        padding: '12px',
        borderRadius: '8px',
        zIndex: 1,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 30,
            height: 4,
            backgroundColor: legendColor,
            marginRight: '10px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '2px',
          }}
        />
        <Typography variant="body2">Assets within risk areas</Typography>
      </Box>
    </Paper>
  );
};


const MapView = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const theme = useTheme();

  // --- UTILITY FUNCTIONS (Unchanged) ---
  const addIconToMap = useCallback((iconUrl, iconId) => {
    if (!map.current) return Promise.reject(new Error("Map is not initialized"));
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = iconUrl;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = img.width; canvas.height = img.height;
        context.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');
        if (map.current) {
          map.current.loadImage(pngUrl, (error, image) => {
            if (error) reject(error);
            else if (map.current) {
              if (map.current.hasImage(iconId)) map.current.removeImage(iconId);
              map.current.addImage(iconId, image, { sdf: false });
              resolve();
            } else reject(new Error("Map uninitialized during image loading"));
          });
        } else reject(new Error("Map uninitialized during image processing"));
      };
      img.onerror = reject;
    });
  }, []);

  const createNodePopupContent = (properties) => {
    const { name, code, demand_transport, demand_power, demand_water } = properties;
    const formatDemandSupply = (value, type) => {
      if (value === undefined) return '';
      const isSupply = value < 0;
      const absValue = Math.abs(value);
      const label = isSupply ? 'Supply' : 'Demand';
      return `<p class="popup-detail"><strong>${type} ${label}:</strong> ${absValue}</p>`;
    };
    return `
      <div class="popup-container popup-node-content">
        <div class="popup-header"><strong class="popup-title">${code || 'Unknown'}</strong></div>
        <div class="popup-body">
          <p class="popup-detail"><strong>Type:</strong> ${name || 'N/A'}</p>
          ${formatDemandSupply(demand_transport, 'Service')}
          ${formatDemandSupply(demand_power, 'Power')}
          ${formatDemandSupply(demand_water, 'Water')}
        </div>
      </div>
    `;
  };
  
  const addPopupEventListeners = useCallback(() => {
    if (!map.current) return;
    if (!popup.current) {
      popup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'custom-popup' });
    }
    const handleNodeMouseEnter = (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      popup.current.setLngLat(coordinates).setHTML(createNodePopupContent(properties)).addTo(map.current);
      map.current.getCanvas().style.cursor = 'pointer';
    };
    const handleMouseLeave = () => {
      map.current.getCanvas().style.cursor = '';
      popup.current.remove();
    };
    const layerId = 'network-nodes';
    if (map.current.getLayer(layerId)) {
      map.current.off('mouseenter', layerId, handleNodeMouseEnter);
      map.current.off('mouseleave', layerId, handleMouseLeave);
      map.current.on('mouseenter', layerId, handleNodeMouseEnter);
      map.current.on('mouseleave', layerId, handleMouseLeave);
    }
  }, []);

  // --- REACT HOOKS ---

  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoibXZwYXNzb3MiLCJhIjoiY2t6em4xcnpwMGJ2bDNicDYyemM0bWU4YyJ9.fR55BgEafulrz4yLfY2oRA';
    if (map.current) return;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
      center: [18.0656, 59.3327],
      zoom: 12,
    });

    map.current.on('load', () => setIsMapLoaded(true));

    return () => {
      setIsMapLoaded(false);
      if (map.current) { map.current.remove(); map.current = null; }
    };
  }, []);

  useEffect(() => {
    if (map.current && isMapLoaded) {
      const newStyle = theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10';
      map.current.setStyle(newStyle);
      map.current.once('styledata', () => {
        setIsMapLoaded(false);
        setTimeout(() => setIsMapLoaded(true), 100);
      });
    }
  }, [theme.palette.mode]);

  useEffect(() => {
    if (isMapLoaded) {
      const addLayers = () => {
        const layersToRemove = ['network-nodes', 'network-lines'];
        const sourcesToRemove = ['network-tiles'];
        layersToRemove.forEach(id => { if (map.current.getLayer(id)) map.current.removeLayer(id); });
        sourcesToRemove.forEach(id => { if (map.current.getSource(id)) map.current.removeSource(id); });
        
        // --- UPDATED TO USE DJANGO MBTILES ENDPOINT ---
        map.current.addSource('network-tiles', {
          type: 'vector',
          tiles: [`${API_BASE_URL}/api/tiles/combined_network/{z}/{x}/{y}.pbf`],
          minzoom: 0,
          maxzoom: 13
        });

        // --- THE CRITICAL CHANGE IS HERE ---
        // This MUST match the actual source layer name in your MBTiles
        const SOURCE_LAYER_NAME = 'combined_network';
        
        map.current.addLayer({
          id: 'network-lines',
          type: 'line',
          source: 'network-tiles', 
          'source-layer': SOURCE_LAYER_NAME,
          filter: ['==', '$type', 'LineString'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ['case', ['==', ['get', 'is_critical'], true], '#d73027', theme.palette.mode === 'dark' ? '#555' : '#D3D3D3'],
            'line-width': ['case', ['==', ['get', 'is_critical'], true], 2.5, 1],
          },
        });

        const getIconName = (baseName) => theme.palette.mode === 'dark' ? `${baseName}-dark` : baseName;
        map.current.addLayer({
          id: 'network-nodes',
          type: 'symbol',
          source: 'network-tiles', 
          'source-layer': SOURCE_LAYER_NAME,
          filter: ['all', ['==', '$type', 'Point'], ['!=', 'name', 'Road']],
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'is_critical'], true],
              [ 'match', ['get', 'name'], 'School', 'school-highlighted', 'Port', 'directions-boat-highlighted', 'Harbour', 'directions-boat-highlighted', 'Household', 'home-highlighted', 'Supermarket', 'local-grocery-store-highlighted', 'Warehouse', 'warehouse-highlighted', 'Farm', 'agriculture-highlighted', 'Power station', 'factory-highlighted', 'Power substation', 'power-highlighted', 'Hospital', 'local-hospital-highlighted', 'Clinic', 'local-hospital-highlighted', 'Pharmacy', 'pharmacy-highlighted', 'Fire station', 'fire-station-highlighted', 'Police station', 'police-highlighted', 'Military base', 'police-highlighted', 'Train station', 'train-highlighted', 'Airport', 'plane-highlighted', 'Government office', 'office-highlighted', 'Courthouse', 'office-highlighted', 'City hall', 'office-highlighted', 'Communication tower', 'communication-tower-highlighted', 'Water reservoir', 'reservoir-highlighted', 'Water plant', 'droplet-highlighted', 'Wastewater plant', 'droplet-highlighted', 'home-highlighted' ],
              [ 'match', ['get', 'name'], 'School', getIconName('school'), 'Port', getIconName('directions-boat'), 'Harbour', getIconName('directions-boat'), 'Household', getIconName('home'), 'Supermarket', getIconName('local-grocery-store'), 'Warehouse', getIconName('warehouse'), 'Farm', getIconName('agriculture'), 'Power station', getIconName('factory'), 'Power substation', getIconName('power'), 'Hospital', getIconName('local-hospital'), 'Clinic', getIconName('local-hospital'), 'Pharmacy', getIconName('pharmacy'), 'Fire station', getIconName('fire-station'), 'Police station', getIconName('police'), 'Military base', getIconName('police'), 'Train station', getIconName('train'), 'Airport', getIconName('plane'), 'Government office', getIconName('office'), 'Courthouse', getIconName('office'), 'City hall', getIconName('office'), 'Communication tower', getIconName('communication-tower'), 'Water reservoir', getIconName('reservoir'), 'Water plant', getIconName('droplet'), 'Wastewater plant', getIconName('droplet'), getIconName('home') ]
            ],
            'icon-size': ['case', ['==', ['get', 'is_critical'], true], 1.1, 1],
            'icon-allow-overlap': true,
          },
          minzoom: 12
        });
        
        addPopupEventListeners();
      };
      
      const allIcons = { ...icons, ...highlightedIcons };
      const iconPromises = Object.entries(allIcons).map(([name, url]) => addIconToMap(url, name));
      
      Promise.all(iconPromises).then(addLayers).catch(error => console.error("Failed to load map icons:", error));
    }
  }, [isMapLoaded, theme.palette.mode, addIconToMap, addPopupEventListeners]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '540px', borderRadius: '8px' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%', borderRadius: 'inherit' }} />
      <MapLegend />
    </div>
  );
};

export default MapView;